import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from '../FirebaseConfig';
import { collection, addDoc, serverTimestamp, query, where, getDocs, doc, getDoc, updateDoc, increment, deleteDoc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { ToastContainer, toast } from 'react-toastify';
import { FaPlus, FaBrain, FaCamera } from 'react-icons/fa';
import axios from 'axios';
import { FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import 'react-toastify/dist/ReactToastify.css';
import BankSelector from '../component/BankSelector';
import UnverifiedAlert from '../component/UnverifiedAlert';

const SellerUpload = (props) => {
  const auth_instance = auth;
  const user = auth_instance.currentUser;
  const navigate = useNavigate();
  const userId = auth_instance.currentUser?.uid;
  
  const [sellerProfile, setSellerProfile] = useState(null);
  const [showProfileEdit, setShowProfileEdit] = useState(false);

  const [products, setProducts] = useState([]);
  const [Error, setError] = useState(false);
  const [product, setProduct] = useState({
    name: '',
    price: '',
    description: '',
    category: '',
    previews: [],
    images: [],
    DeliveryAddress: '',
    DeliveryFee: '',
    PhoneNumber: '',
    condition: '',
    defects: '',
    needsVerification: true, // NEW: Track if seller wants verification
  });

  const [loading, setLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false); // NEW: Modal for verification choice

  // AI Tips State
  const [aiTips, setAiTips] = useState(null);
  const [showAiTips, setShowAiTips] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [selectedImageBase64, setSelectedImageBase64] = useState('');

  const canvasRef = useRef(null);

  useEffect(() => {
    if (!props.isSigned) {
      const timer = setTimeout(() => {
        navigate('/Login');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [navigate, props.isSigned]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const docRef = doc(db, "profiles", userId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setSellerProfile({ id: docSnap.id, ...docSnap.data() });
        }
      } catch (err) {
        console.error("Failed to fetch seller profile", err);
      }
    };

    if (userId) {
      fetchProfile();
    }
  }, [userId]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();

    // SECURITY GUARD: Stop blocked users from editing profile
    if (sellerProfile?.blocked) {
      toast.error("Your account is suspended. You cannot edit your profile.");
      return;
    }

    setProfileSaving(true);

    try {
      if (!sellerProfile?.id) {
        toast.error("Seller profile not found.");
        setProfileSaving(false);
        return;
      }

      const requiredFields = ['name', 'gender', 'bankName', 'accountNumber', 'accountName'];
      for (let field of requiredFields) {
        if (!sellerProfile[field] || sellerProfile[field].toString().trim() === '') {
          toast.error(`Please fill the "${field}" field.`);
          setProfileSaving(false);
          return;
        }
      }

      // Safely split bank info
      let bankName = '';
      let bankCode = '';
      if (sellerProfile.bankName?.includes('|')) {
        [bankName, bankCode] = sellerProfile.bankName.split('|');
      } else {
        bankName = sellerProfile.bankName;
        bankCode = '';
      }

      const profileRef = doc(db, "profiles", sellerProfile.id);
      await updateDoc(profileRef, {
        name: sellerProfile.name,
        gender: sellerProfile.gender,
        profilePic: sellerProfile.profilePic || "",
        bankName,
        bankCode,
        university: sellerProfile.university,
        storeName: sellerProfile.storeName,
        accountNumber: sellerProfile.accountNumber,
        accountName: sellerProfile.accountName,
      });

      toast.success("Profile updated successfully!");
      setShowProfileEdit(false);

      const updatedSnap = await getDoc(profileRef);
      if (updatedSnap.exists()) {
        setSellerProfile({ id: updatedSnap.id, ...updatedSnap.data() });
      }

    } catch (err) {
      console.error("Error updating profile:", err);
      toast.error("Failed to update profile.");
    }

    setProfileSaving(false);
  };

  const handleImageUpload = async (e) => {
    // SECURITY GUARD
    if (sellerProfile?.blocked) {
      toast.error("Action blocked. Account suspended.");
      return;
    }

    const file = e.target.files[0];
    if (!file) return;

    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", "unsigned_ibuyunn");

    try {
      toast.info("Uploading image...");
      const res = await fetch("https://api.cloudinary.com/v1_1/dm8uirujp/upload", {
        method: "POST",
        body: data,
      });

      const result = await res.json();
      const imageUrl = result.secure_url;

      // Update Firestore
      const docRef = doc(db, "profiles", sellerProfile.id);
      await updateDoc(docRef, { profilePic: imageUrl });

      setSellerProfile((prev) => ({ ...prev, profilePic: imageUrl }));
      toast.success("Profile picture updated!");
    } catch (err) {
      console.error("Image upload error:", err);
      toast.error("Image upload failed.");
    }
  };

  const fetchProducts = async () => {
    try {
      const q = query(collection(db, "Products"), where("sellerId", "==", userId));
      const querySnapshot = await getDocs(q);
      const productsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProducts(productsList);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error("Error fetching products: ", error);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchProducts();
    }
  }, [userId]);

  const handleDelete = async (id) => {
    // SECURITY GUARD: Stop blocked users from deleting
    if (sellerProfile?.blocked) {
      toast.error("Your account is suspended. Action denied.");
      return;
    }

    if (!id) {
      console.error("Invalid product ID passed to handleDelete");
      return;
    }

    try {
      await deleteDoc(doc(db, 'Products', id));
      toast.warn("Product deleted successfully");
      await fetchProducts();
    } catch (error) {
      console.error("Error deleting product: ", error);
      toast.error("Failed to delete product");
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    // SECURITY GUARD: Stop blocked users from editing
    if (sellerProfile?.blocked) {
      toast.error("Your account is suspended. You cannot edit products.");
      setEditingProduct(null);
      return;
    }

    setEditLoading(true);
    try {
      const productRef = doc(db, 'Products', editingProduct.id);
      await updateDoc(productRef, {
        name: editingProduct.name,
        price: parseFloat(editingProduct.price),
        description: editingProduct.description,
        category: editingProduct.category,
        DeliveryAddress: editingProduct.DeliveryAddress,
        phoneNumber: editingProduct.PhoneNumber || editingProduct.phoneNumber,
        condition: editingProduct.condition,
        defects: editingProduct.defects || '',
        DeliveryFee: parseFloat(editingProduct.DeliveryFee) || 0,
        approved: false,
      });
      toast.success("Product updated! It will be re-reviewed.");
      setEditingProduct(null);
      await fetchProducts();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update product.");
    }
    setEditLoading(false);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': [] },
    onDrop: async (acceptedFiles) => {
      // SECURITY GUARD
      if (sellerProfile?.blocked) {
        toast.error("Action blocked. Account suspended.");
        return;
      }

      const file = acceptedFiles[0];
      if (!file || !(file instanceof File)) {
        toast.error("Invalid file selected.");
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', 'unsigned_ibuyunn');

      try {
        toast.info("Uploading image...");
        const res = await axios.post(
          'https://api.cloudinary.com/v1_1/dm8uirujp/image/upload',
          formData
        );

        setProduct(prev => ({
          ...prev,
          imageUrl: res.data.secure_url
        }));

        const reader = new FileReader();
        reader.onload = (e) => {
          setSelectedImageBase64(e.target.result);
        };
        reader.readAsDataURL(file);

        toast.success("Image uploaded successfully!");
      } catch (err) {
        console.error("Image upload error:", err.response?.data || err);
        toast.error("Image upload failed.");
      }
    }
  });

  const handleChange = (e) => {
    setProduct({ ...product, [e.target.name]: e.target.value });
  };

  // NEW: Handle verification choice
  const handleVerificationChoice = async (needsVerification) => {
    setShowVerificationModal(false);

    if (needsVerification) {
      // User wants verification badge - redirect to verification page
      toast.info("Redirecting to verification...");
      navigate('/Verify'); 
      return;
    }

    // User chose to skip verification - continue with submission
    setProduct(prev => ({ ...prev, needsVerification: false }));
    toast.warning("⚠️ Listing without verification: Your product won't have a verification badge and cannot be promoted.");

    // Continue with form submission
    await submitProduct(false);
  };

  const submitProduct = async (needsVerification) => {
    setLoading(true);

    try {
      const requiredFields = [
        'name',
        'price',
        'description',
        'category',
        'DeliveryAddress',
        'PhoneNumber',
      ];

      for (let field of requiredFields) {
        if (!product[field] || product[field].trim() === '') {
          toast.error(`Please fill the "${field}" field.`);
          setLoading(false);
          return;
        }
      }

      if (product.condition === 'Used' && (!product.defects || product.defects.trim() === '')) {
        toast.error("Please describe any scratches or defects for used products.");
        setLoading(false);
        return;
      }

      if (!product.imageUrl) {
        toast.error("Please upload an image.");
        setLoading(false);
        return;
      }

      if (
        !sellerProfile?.bankName ||
        !sellerProfile?.accountName ||
        !sellerProfile?.accountNumber
      ) {
        toast.error("Please complete your seller profile (bank info).");
        setLoading(false);
        return;
      }

      const sellerRef = doc(db, "profiles", user.uid);
      const sellerSnap = await getDoc(sellerRef);

      if (!sellerSnap.exists()) {
        toast.error("Seller profile not found.");
        setLoading(false);
        return;
      }

      const sellerData = sellerSnap.data();
      const isFirstProduct = !sellerData.hasListed;   
      const wasReferred = !!sellerData.referredBy;    

      if (isFirstProduct && wasReferred) {
        const referrerId = sellerData.referredBy;

        try {
          const referrerRef = doc(db, "profiles", referrerId);
          await updateDoc(referrerRef, {
            totalEarnings: increment(200),
            sellerReferrals: increment(1),  
          });
          toast.success("Your referrer earned ₦200! 🎉");
        } catch (referrerError) {
          console.error("Error crediting referrer:", referrerError);
        }
      }

      try {
        await updateDoc(sellerRef, {
          hasListed: true,  
        });
      } catch (err) {
        console.error("Error marking hasListed:", err);
      }

      // NEW: Add verification status to product
      const isVerified = sellerProfile?.verification_status === 'approved';
      const productNeedsVerification = needsVerification && isVerified;

      await addDoc(collection(db, 'Products'), {
        name: product.name,
        price: parseFloat(product.price),
        description: product.description,
        category: product.category,
        imageUrl: product.imageUrl,
        sellerId: userId,
        DeliveryAddress: product.DeliveryAddress,
        phoneNumber: product.PhoneNumber,
        approved: false,
        university: sellerProfile.university,
        storeName: sellerProfile.storeName,
        BankName: sellerProfile.bankName,
        AccountName: sellerProfile.accountName,
        AccountNumber: sellerProfile.accountNumber,
        createdAt: serverTimestamp(),
        createdBy: auth_instance.currentUser?.email || 'anonymous',
        condition: product.condition,
        defects: product.defects,
        DeliveryFee: parseFloat(product.DeliveryFee) || 0,
        isVerified: productNeedsVerification, // NEW: Track if product has verification badge
        canBePromoted: productNeedsVerification, // NEW: Only verified products can be promoted
      });

      setProduct({
        name: '',
        price: '',
        description: '',
        category: '',
        imageUrl: '',
        DeliveryAddress: '',
        PhoneNumber: '',
        DeliveryFee: '',
        condition: '',
        defects: '',
        needsVerification: true,
      });
      setSelectedImageBase64('');
      setAiTips(null);
      setShowAiTips(false);

      setShowPopup(true);
      toast.success("Product submitted successfully!");

      await fetchProducts();
      setShowForm(false);

    } catch (err) {
      console.error(err);
      toast.error("Error uploading product.");
    }

    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // SECURITY GUARD: Stop blocked users from uploading
    if (sellerProfile?.blocked) {
      toast.error("Your account is suspended. You cannot list new products.");
      return;
    }

    const isVerified = sellerProfile?.verification_status === 'approved';

    // NEW: If already verified, go straight to submission
    if (isVerified) {
      await submitProduct(true);
    } else {
      // NEW: Show modal asking if they want to get verified
      setShowVerificationModal(true);
    }
  };

  const isVerified = sellerProfile?.verification_status === 'approved';

  return props.isSigned ? (
    <div className="text-white bg-transparent min-h-screen relative">
      <section className="max-w-6xl mx-auto mt-8 px-4 py-8">
        
        {/* We only show UnverifiedAlert if they are not blocked */}
        {!sellerProfile?.blocked && <UnverifiedAlert />}

        {/* This is the Big Red Banner showing only if blocked is true */}
        {sellerProfile?.blocked && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-900/40 border-2 border-red-500 rounded-2xl p-6 mb-8 text-center shadow-[0_0_20px_rgba(239,68,68,0.3)]"
          >
            <h2 className="text-3xl font-extrabold text-red-500 mb-2 uppercase tracking-widest">🚫 Account Suspended</h2>
            <p className="text-red-200 text-lg">Your seller privileges have been disabled by the administration due to policy violations.</p>
            <p className="text-red-300/70 mt-2 text-sm">You can no longer edit your profile, add new products, or modify existing listings.</p>
          </motion.div>
        )}

        <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/10">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-6">
            <h3 className="text-3xl font-bold mb-4 lg:mb-0 text-white">Seller Dashboard</h3>
            <div className="flex gap-3">
              {/* Hide the Edit Profile button entirely if they are blocked */}
              {!showProfileEdit && !sellerProfile?.blocked ? (
                <button
                  onClick={() => setShowProfileEdit(true)}
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg font-medium"
                >
                  <FaEdit /> Edit Profile
                </button>
              ) : sellerProfile?.blocked ? (
                // Show a disabled button instead to let them know it's locked
                <button
                  disabled
                  className="flex items-center gap-2 bg-gray-600/50 text-gray-400 px-6 py-3 rounded-xl cursor-not-allowed font-medium"
                >
                  <FaEdit /> Profile Locked
                </button>
              ) : null}
            </div>
          </div>

          {!showProfileEdit ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-1 flex flex-col items-center text-center">
                <div className="relative">
                  <img
                    src={sellerProfile?.profilePic || "https://placehold.co/160x160/2d3748/ffffff?text=Profile"}
                    alt="Profile"
                    className={`w-32 h-32 object-cover rounded-full border-4 shadow-xl ${sellerProfile?.blocked ? 'border-red-500/50 opacity-50' : 'border-green-400/30'}`}
                  />
                  {!sellerProfile?.blocked && (
                    <div className="absolute -bottom-2 -right-2 bg-green-500 p-2 rounded-full">
                      <FaCamera className="text-white text-sm" />
                    </div>
                  )}
                </div>
                <h4 className="text-xl font-semibold mt-4 text-white">{sellerProfile?.name || 'N/A'}</h4>
                <p className={`${sellerProfile?.blocked ? 'text-red-400' : 'text-green-400'} font-medium`}>
                  {sellerProfile?.blocked ? 'Suspended Seller' : (sellerProfile?.university || 'University Not Set')}
                </p>
              </div>
              <div className="space-y-4 lg:col-span-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-white/10 p-4 rounded-xl border border-white/20">
                    <p className="text-gray-400 text-sm uppercase tracking-wide">Email</p>
                    <p className="text-white font-semibold break-all text-sm">
                      {sellerProfile?.userEmail?.substring(0, 22) || auth_instance.currentUser?.email?.substring(0, 22) || 'N/A'}
                    </p>
                  </div>
                  <div className="bg-white/10 p-4 rounded-xl border border-white/20">
                    <p className="text-gray-400 text-sm uppercase tracking-wide">Gender</p>
                    <p className="text-white font-semibold text-sm">{sellerProfile?.gender || 'Not set'}</p>
                  </div>
                  <div className="bg-white/10 p-4 rounded-xl border border-white/20">
                    <p className="text-gray-400 text-sm uppercase tracking-wide">University</p>
                    <p className="text-white font-semibold text-sm">{sellerProfile?.university || 'Not set'}</p>
                  </div>
                  <div className="bg-white/10 p-4 rounded-xl border border-white/20">
                    <p className="text-gray-400 text-sm uppercase tracking-wide">Store Name</p>
                    <p className="text-white font-semibold text-sm">{sellerProfile?.storeName || 'Not set'}</p>
                  </div>
                  <div className="bg-white/10 p-4 rounded-xl border border-white/20 sm:col-span-2 lg:col-span-1">
                    <p className="text-gray-400 text-sm uppercase tracking-wide">Bank Account</p>
                    <p className="text-white font-semibold text-xs">{sellerProfile?.bankName || 'Not set'}</p>
                    <p className="text-white font-semibold text-xs">{sellerProfile?.accountName || 'Not set'}</p>
                    <p className="text-white font-semibold text-xs">****{sellerProfile?.accountNumber?.slice(-4) || '****'}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleProfileUpdate} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-1 flex flex-col items-center space-y-4">
                <img
                  src={sellerProfile?.profilePic || "https://placehold.co/128x128/2d3748/ffffff?text=Profile"}
                  alt="Profile"
                  className="w-24 h-24 object-cover rounded-full shadow-lg"
                />
                <label className="block text-sm font-medium text-gray-400">Change Profile Picture</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-700 file:text-white hover:file:bg-gray-600"
                />
              </div>
              <div className="space-y-4 md:col-span-1">
                <input
                  type="text"
                  value={sellerProfile?.name || ''}
                  onChange={(e) => setSellerProfile({ ...sellerProfile, name: e.target.value })}
                  placeholder="Full Name"
                  className="w-full p-4 rounded-xl bg-white/10 text-white border border-white/20 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <select
                  value={sellerProfile?.gender || ''}
                  onChange={(e) => setSellerProfile({ ...sellerProfile, gender: e.target.value })}
                  className="w-full p-4 rounded-xl bg-white/10 text-white border border-white/20 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                <input
                  type="text"
                  value={sellerProfile?.university || ''}
                  onChange={(e) => setSellerProfile({ ...sellerProfile, university: e.target.value })}
                  placeholder="University (Write in Full)"
                  className="w-full p-4 rounded-xl bg-white/10 text-white border border-white/20 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <input
                  type="text"
                  value={sellerProfile?.storeName || ''}
                  onChange={(e) => setSellerProfile({ ...sellerProfile, storeName: e.target.value })}
                  placeholder="Store Name"
                  className="w-full p-4 rounded-xl bg-white/10 text-white border border-white/20 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div className="md:col-span-2 space-y-4">
                <BankSelector
                  value={{
                    bankName: sellerProfile?.bankName || "",
                    bankCode: sellerProfile?.bankCode || "",
                  }}
                  onChange={(val) => setSellerProfile({ ...sellerProfile, ...val })}
                />
                <input
                  type="text"
                  value={sellerProfile?.accountNumber || ''}
                  onChange={(e) => setSellerProfile({ ...sellerProfile, accountNumber: e.target.value })}
                  placeholder="Account Number"
                  className="w-full p-4 rounded-xl bg-white/10 text-white border border-white/20 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <input
                  type="text"
                  value={sellerProfile?.accountName || ''}
                  onChange={(e) => setSellerProfile({ ...sellerProfile, accountName: e.target.value })}
                  placeholder="Account Name"
                  className="w-full p-4 rounded-xl bg-white/10 text-white border border-white/20 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div className="md:col-span-2 flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-xl hover:from-green-700 hover:to-green-800 transition-all shadow-lg font-medium flex items-center justify-center gap-2"
                  disabled={profileSaving}
                >
                  <FaSave /> {profileSaving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowProfileEdit(false)}
                  className="flex-1 bg-gray-600 text-white px-6 py-3 rounded-xl hover:bg-gray-700 transition-all shadow-lg font-medium flex items-center justify-center gap-2"
                >
                  <FaTimes /> Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Welcome Message */}
      <div className="flex flex-col z-50 items-center justify-center text-center mt-12">
        <h2 className="text-3xl font-bold mb-2">Welcome Back, Seller!</h2>
        <p className="text-gray-400 mb-4">Manage your products and start selling with ease.</p>

        {/* NEW: Updated button logic */}
        {sellerProfile?.blocked ? (
          <button
            onClick={() => toast.error("Account Suspended. Cannot add products.")}
            className="flex items-center gap-2 bg-red-900/50 text-red-300 border-2 border-red-500 px-5 py-2 rounded-2xl shadow-lg cursor-not-allowed"
            disabled
          >
            🚫 Blocked from Adding Products
          </button>
        ) : (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex z-50 items-center gap-2 bg-transparent border-2 border-emerald-500 hover:bg-emerald-700 text-white px-5 py-2 rounded-2xl shadow-lg transition-all"
          >
            <FaPlus /> {showForm ? 'Hide Form' : 'Add Product'}
          </button>
        )}
      </div>

      {/* NEW: Verification Choice Modal */}
      <AnimatePresence>
        {showVerificationModal && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-gradient-to-br from-slate-800 to-slate-900 border border-cyan-500/30 p-8 rounded-3xl shadow-2xl max-w-2xl mx-4"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 200 }}
            >
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                ✓ Get Verified to List with Badge
              </h3>
              
              <p className="text-slate-300 mb-6 leading-relaxed">
                You're about to list a new product. To add a verification badge (which increases buyer trust and allows promotion), you'll need to complete seller verification first.
              </p>

              <div className="space-y-4 mb-8">
                {/* Option 1: With Verification */}
                <div className="bg-green-500/10 border-2 border-green-500/50 rounded-2xl p-6 cursor-pointer hover:bg-green-500/20 transition-all" onClick={() => handleVerificationChoice(true)}>
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">✓</div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-green-400 mb-1">Get Verified & List with Badge</h4>
                      <p className="text-slate-300 text-sm mb-3">
                        Complete seller verification to unlock the verification badge and promotion features for this product.
                      </p>
                      <ul className="text-slate-400 text-xs space-y-1">
                        <li>✅ Redirects to verification page</li>
                        <li>✅ Complete verification process</li>
                        <li>✅ Return to list your product with badge</li>
                        <li>✅ Unlock product promotion</li>
                      </ul>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVerificationChoice(true);
                    }}
                    className="mt-4 w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-semibold transition-all"
                  >
                    Get Verified First →
                  </button>
                </div>

                {/* Option 2: Skip Verification */}
                <div className="bg-amber-500/10 border-2 border-amber-500/50 rounded-2xl p-6 cursor-pointer hover:bg-amber-500/20 transition-all" onClick={() => handleVerificationChoice(false)}>
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">⚠️</div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-amber-400 mb-1">List without Badge (Skip)</h4>
                      <p className="text-slate-300 text-sm mb-3">
                        Your product will be listed immediately without verification badge.
                      </p>
                      <ul className="text-slate-400 text-xs space-y-1">
                        <li>❌ No verification badge</li>
                        <li>❌ Cannot promote this product</li>
                        <li>⚠️ Lower buyer confidence</li>
                        <li>⚠️ Less visibility in search</li>
                      </ul>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVerificationChoice(false);
                    }}
                    className="mt-4 w-full bg-amber-600 hover:bg-amber-700 text-white py-2 rounded-lg font-semibold transition-all"
                  >
                    Skip & List Now
                  </button>
                </div>
              </div>

              <p className="text-slate-400 text-xs text-center">
                You can always add verification later by completing your seller verification process.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Product Upload Form */}
      <AnimatePresence>
        {showForm && !sellerProfile?.blocked && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="max-w-4xl mx-auto mb-12 bg-transparent backdrop-blur-lg p-8 rounded-3xl shadow-2xl border border-gray-700"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <h3 className="text-xl font-bold text-white mb-4">Product Details</h3>
              <input
                type="text"
                name="name"
                value={product.name}
                onChange={handleChange}
                placeholder="Product Name"
                className="w-full rounded-xl bg-gray-700/50 p-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <div className="grid md:grid-cols-2 gap-6">
                <input
                  type="number"
                  name="price"
                  value={product.price}
                  onChange={handleChange}
                  placeholder="Price"
                  className="w-full rounded-xl bg-gray-700/50 p-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <select
                  name="category"
                  value={product.category}
                  onChange={handleChange}
                  className="w-full rounded-xl bg-gray-700/50 p-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Category</option>
                  <option value="phones">Phones</option>
                  <option value="laptops">Laptops</option>
                  <option value="gadgets">Gadgets</option>
                  <option value="clothes">Clothes</option>
                  <option value="others">Others</option>
                </select>
              </div>
              <select
                name="condition"
                value={product.condition}
                onChange={handleChange}
                className="w-full rounded-xl bg-gray-700/50 p-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Condition</option>
                <option value="New">New</option>
                <option value="Used">Used</option>
              </select>
              {product.condition === 'Used' && (
                <textarea
                  name="defects"
                  value={product.defects}
                  onChange={handleChange}
                  placeholder="Describe any scratches or defects"
                  className="w-full rounded-xl bg-gray-700/50 p-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              )}
              <textarea
                name="DeliveryAddress"
                value={product.DeliveryAddress}
                onChange={handleChange}
                placeholder="Pick up station"
                className="w-full rounded-xl bg-gray-700/50 p-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                name="PhoneNumber"
                value={product.PhoneNumber}
                onChange={handleChange}
                placeholder="Phone number"
                className="w-full rounded-xl bg-gray-700/50 p-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="number"
                name="DeliveryFee"
                value={product.DeliveryFee}
                onChange={handleChange}
                placeholder="Delivery Fee (optional)"
                className="w-full rounded-xl bg-gray-700/50 p-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              {/* Image Upload Section */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Product Image</label>
                <div
                  {...getRootProps()}
                  className="border-2 border-dashed border-gray-600 p-8 text-center rounded-2xl cursor-pointer hover:bg-gray-700 transition-colors"
                >
                  <input {...getInputProps({ accept: 'image/*' })} />
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt="Product Preview"
                      className="w-32 h-32 object-cover rounded-xl mx-auto shadow-md"
                    />
                  ) : (
                    <p className="text-gray-400">Drag & drop image here, or click to select</p>
                  )}
                </div>
              </div>

              <textarea
                name="description"
                value={product.description}
                onChange={handleChange}
                placeholder="Short Description"
                className="w-full rounded-xl bg-gray-700/50 p-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />

              <button
                type="submit"
                className="w-full bg-green-600 text-white px-6 py-3 rounded-full hover:bg-green-700 transition-colors shadow-lg font-bold"
                disabled={loading}
              >
                {loading ? 'Uploading...' : 'Submit for Approval'}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Product List */}
      <div className="mt-16 max-w-6xl mx-auto px-4">
        <h3 className="text-2xl font-semibold mb-6">Your Products</h3>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map(prod => (
            <li key={prod.id} className="bg-white/10 backdrop-blur-lg p-4 rounded-2xl shadow hover:scale-105 transition-transform">
              {editingProduct?.id === prod.id ? (
                <form onSubmit={handleEditSubmit} className="space-y-3">
                  <h4 className="text-white font-bold text-lg mb-2">Editing: {prod.name}</h4>
                  <input
                    type="text"
                    value={editingProduct.name}
                    onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                    placeholder="Product Name"
                    className="w-full rounded-xl bg-gray-700/50 p-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                  <input
                    type="number"
                    value={editingProduct.price}
                    onChange={(e) => setEditingProduct({ ...editingProduct, price: e.target.value })}
                    placeholder="Price"
                    className="w-full rounded-xl bg-gray-700/50 p-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                  <textarea
                    value={editingProduct.description}
                    onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                    placeholder="Description"
                    className="w-full rounded-xl bg-gray-700/50 p-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={editLoading}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-xl font-bold transition"
                    >
                      {editLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingProduct(null)}
                      className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-xl font-bold transition"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <img src={prod.imageUrl} alt={prod.name} className="w-full h-48 object-cover rounded-xl mb-4 shadow" />
                  <h4 className="text-lg font-bold">{prod.name}</h4>
                  <p className="text-green-300 font-semibold">₦{prod.price?.toLocaleString()}</p>
                  <p className="text-xs text-gray-300 mt-1">{prod.category}</p>
                  
                  {/* NEW: Show verification badge status */}
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {prod.isVerified && (
                      <span className="bg-green-500/20 border border-green-500 text-green-300 text-xs px-2 py-1 rounded-lg font-semibold">
                        ✓ Verified
                      </span>
                    )}
                    {!prod.isVerified && (
                      <span className="bg-gray-500/20 border border-gray-500 text-gray-300 text-xs px-2 py-1 rounded-lg">
                        Unverified
                      </span>
                    )}
                  </div>

                  <p className="text-xs mt-2">
                    {prod.approved 
                      ? <span className="text-green-400">✅ Approved</span> 
                      : <span className="text-yellow-400">⏳ Pending approval</span>
                    }
                  </p>
                  <div className="flex gap-2 mt-3">
                    {/* The disabled={} attribute will grey out the buttons if blocked */}
                    <button
                      disabled={sellerProfile?.blocked}
                      className={`flex-1 py-2 rounded-lg text-white text-sm font-medium transition ${sellerProfile?.blocked ? 'bg-gray-600 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                      onClick={() => setEditingProduct(prod)}
                    >
                      <FaEdit className="inline mr-1" /> Edit
                    </button>
                    <button
                      disabled={sellerProfile?.blocked}
                      className={`flex-1 py-2 rounded-lg text-white text-sm font-medium transition ${sellerProfile?.blocked ? 'bg-gray-600 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
                      onClick={() => handleDelete(prod.id)}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Popup */}
      <AnimatePresence>
        {showPopup && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white p-6 rounded-2xl shadow-2xl max-w-sm text-center"
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.7, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 200 }}
            >
              <h3 className="text-xl font-bold text-green-600 mb-2">✅ Product Submitted</h3>
              <p className="text-gray-600">Your product has been submitted for approval.</p>
              <button
                onClick={() => setShowPopup(false)}
                className="mt-4 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Got it!
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ToastContainer />
    </div>
  ) : null;
};

export default SellerUpload;